import { useState } from "react";
import { useTranslation } from "react-i18next";
import useVideoTranscode from "../../../../hooks/useVideoTranscode";
import SvgIcon from "../../../../parts/SvgIcon";
import { truncateVideoText } from "../../../../utils/string-helpers";

const fileLimit = 200;

const VideoUpload = ({
  form,
  video,
  explanationVideo = false,
  goBack = () => {},
}) => {
  const { t } = useTranslation("coaching-portal");
  const hasVideoUrl = typeof video === "string";
  const [videoUrl, setVideoUrl] = useState(hasVideoUrl ? video : "");
  const [fileName, setFileName] = useState(video?.fileName || "");
  const [deleteVideo, setDeleteVideo] = useState(false);
  const [isOutOfLimit, setIsOutOfLimit] = useState(false);
  // Compresses on pick and swaps the input's FileList, so the form carries the
  // smaller file and the server knows whether it still needs MediaConvert.
  const {
    onChange: onVideoPicked,
    preparing,
    progress,
    tooBig,
    reset,
    transcoded,
    dimensions,
  } = useVideoTranscode({ maxUploadMb: fileLimit });

  const cleanVideoField = () => {
    // Also empties the input's FileList and the transcode outcome, so the hidden
    // fields stop claiming a video is attached.
    reset();
    setFileName("");
    setDeleteVideo(video?.fileName || "");
  };

  const showDisableVideoInputClassUrl = videoUrl ? "disable-video-input" : "";

  return (
    <>
      {explanationVideo ? (
        <div className="explanation-video">
          <SvgIcon src="/img/new-icons/back-button.svg" onClickFn={goBack} />
          <div>
            <p className="s2">{t("customWorkflows.addExplanationVideo")}</p>
          </div>
        </div>
      ) : null}
      <input name="deleteVideo" value={deleteVideo} type="hidden" />
      <div className="form-row-item">
        <span className={`sub-label ${showDisableVideoInputClassUrl}`}>
          {t("customWorkflows.uploadVideoLabel")}
          {(isOutOfLimit || tooBig) && ` - ${t("customWorkflows.fileTooBig")}`}
        </span>
        <label
          className={`video-input${
            fileName ? " uploaded" : ""
          } ${showDisableVideoInputClassUrl}`}
        >
          <div>
            <SvgIcon
              src={
                fileName
                  ? "/img/new-icons/Video.svg"
                  : "/img/new-icons/upload-file.svg"
              }
              noFilterOnLight={!fileName}
            />{" "}
            {fileName
              ? `${
                  truncateVideoText(fileName) ||
                  truncateVideoText(
                    fileName.split("\\")[fileName.split("\\").length - 1]
                  )
                }`
              : t("customWorkflows.chooseFile")}
          </div>
          {fileName && (
            <button
              type="button"
              data-button-type="secondary"
              data-icon-only={true}
              data-icon={true}
              onClick={(e) => {
                e.preventDefault();
                cleanVideoField();
              }}
            >
              <SvgIcon
                src="/img/new-icons/red-trash.svg"
                noFilterOnLight={true}
              />
            </button>
          )}
          <input
            className={`text-input file-input-other-form ${showDisableVideoInputClassUrl}`}
            type="file"
            name="videoFile"
            disabled={!!videoUrl || preparing}
            onChange={async (e) => {
              await onVideoPicked(e);
              setIsOutOfLimit(false);
              setFileName(e.target.value);
            }}
            form={form}
          />
        </label>
        <input
          type="hidden"
          name="videoTranscoded"
          value={transcoded == null ? "" : String(transcoded)}
          form={form}
        />
        <input
          type="hidden"
          name="videoWidth"
          value={dimensions?.width ?? ""}
          form={form}
        />
        <input
          type="hidden"
          name="videoHeight"
          value={dimensions?.height ?? ""}
          form={form}
        />
        {preparing && <progress value={progress} max={1} />}
      </div>

      <div className="divider">
        <span className="line" />
        <p className="p5">{t("customWorkflows.or")}</p>
        <span className="line" />
      </div>

      <div className="form-row-item">
        <label
          className={`sub-label ${fileName ? "disable-video-input" : ""}`}
          htmlFor="videoUrl"
        >
          {t("customWorkflows.enterUrl")}
        </label>
        <input
          className="url-input"
          type="text"
          name="videoUrl"
          placeholder={
            fileName ? "" : t("customWorkflows.pasteYoutubePlaceholder")
          }
          value={videoUrl || ""}
          onChange={(e) => setVideoUrl(e.target.value)}
          disabled={fileName}
          form={form}
        />
      </div>
    </>
  );
};

export default VideoUpload;
